import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

export function VersionHistoryModal({ open, onClose, blueprint }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !blueprint) return;
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API}/blueprints/${blueprint.id}/versions`);
        setVersions(resp.data);
      } catch {
        toast.error("Failed to load version history");
      } finally {
        setLoading(false);
      }
    };
    fetchVersions();
  }, [open, blueprint?.id]);

  if (!blueprint) return null;

  const fieldLabels = {
    machine_number: "Machine #",
    blueprint_type: "Type",
    notes: "Notes",
    status: "Status",
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg" aria-describedby="version-history-desc">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-[#0F172A] flex items-center gap-2">
            <History size={18} /> Version History
          </DialogTitle>
          <p id="version-history-desc" className="text-xs text-[#64748B] font-mono mt-1">
            {blueprint.original_filename} &middot; Current: v{blueprint.version || 1}
          </p>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto" data-testid="version-history-list">
          {loading ? (
            <div className="py-8 text-center text-[#64748B] text-sm">Loading...</div>
          ) : versions.length === 0 ? (
            <div className="py-8 text-center text-[#64748B] text-sm">
              No previous versions. This is the original document.
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="border border-[#E2E8F0] p-3 bg-[#FAFAFA]"
                  data-testid={`version-entry-${v.version}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-[#2563EB] font-mono">
                      v{v.version}
                    </span>
                    <span className="text-xs text-[#64748B]">
                      {new Date(v.changed_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {Object.entries(v.changes || {}).map(([field, oldValue]) => (
                      <div key={field} className="text-xs">
                        <span className="text-[#64748B]">{fieldLabels[field] || field}:</span>
                        <span className="ml-1 text-[#DC2626] line-through">{oldValue || "(empty)"}</span>
                      </div>
                    ))}
                  </div>
                  {v.changed_by_name && (
                    <div className="text-xs text-[#94A3B8] mt-1">by {v.changed_by_name}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
